import { Link, useParams } from "react-router-dom";
import mixpanel from "mixpanel-browser";
import { useReactiveVar } from "@apollo/client";
import { useFeature } from "@optimizely/react-sdk";

import {
  EOrganizationType,
  IPropertyUnitData,
} from "../../interfaces/interfaces";
import { organizationIdNameVar, userIdVar } from "../apollo/LocalState";
import {
  mapSubscriptionStatus,
  quantityDisplay,
  renderSideLine,
  renderTags,
} from "../../utils/utils";
import Arrow from "../../img/Arrow.svg";
import { isReconfigurationInProgress } from "../../utils/reconfigurationUtils";

export interface UnitCardProps extends IPropertyUnitData {
  enableBrilliantMaxSubscription?: boolean;
}

const renderTenants = ({
  numInvitedResidents,
  numTenants,
}: {
  numInvitedResidents: number;
  numTenants: number;
}) => {
  const totalResidents = numTenants + numInvitedResidents;
  return (
    <div className="flex-display">
      {totalResidents !== 0 && (
        <>
          <div className="margin-right-very-small">/</div>
          <div className="margin-right-very-small">{totalResidents}</div>
          <div className="margin-right-very-small">
            {quantityDisplay(totalResidents, "Resident")}
          </div>
        </>
      )}
    </div>
  );
};

const UnitCard: React.FC<UnitCardProps> = ({
  alerts,
  enableBrilliantMaxSubscription = false,
  propertyId,
  name,
  isHubless,
  numBrilliants,
  numThirdpartyIntegrations,
  numTenants,
  numInvitedResidents,
  reconfigurationState,
  subscription,
  warnings,
}) => {
  const userId = useReactiveVar(userIdVar);
  const organizationIdName = useReactiveVar(organizationIdNameVar);
  const params = useParams();
  const { organizationId } = params;
  const [isFeatureEnabled] = useFeature("brilliant_max_subscription");
  const isBrilliantMaxEnabled =
    isFeatureEnabled || enableBrilliantMaxSubscription;
  const hasAlerts = alerts.length > 0;
  const hasWarnings = warnings && warnings.length > 0;
  const isOccupied = numTenants > 0;
  const isInReconfig = isReconfigurationInProgress(reconfigurationState);
  const renderDescription =
    isInReconfig &&
    organizationIdName.organizationType !==
      EOrganizationType.PRO_VIEW_ORGANIZATION ? (
      <div className="unit-devices unit-description-small">
        <div className="margin-right-very-small">
          <i> RECONFIGURING...</i>
        </div>
      </div>
    ) : (
      <div className="unit-devices unit-description-small">
        {!isHubless && (
          <>
            <div className="margin-right-very-small">{numBrilliants}</div>
            <div className="margin-right-very-small">
              {quantityDisplay(numBrilliants, "BRILLIANT")}
            </div>
            <div className="margin-right-very-small">/</div>
          </>
        )}
        <div className="margin-right-very-small">
          {numThirdpartyIntegrations}
        </div>
        <div className="margin-right-very-small">
          {quantityDisplay(numThirdpartyIntegrations, "Integration")}
        </div>
        {organizationIdName.organizationType !==
          EOrganizationType.PRO_VIEW_ORGANIZATION &&
          renderTenants({
            numInvitedResidents,
            numTenants,
          })}
      </div>
    );

  const vacancyTagClass = `unit-status unit-tag ${
    isOccupied ? "unit-status-occupied" : "unit-status-vacant"
  }`;
  return (
    <button
      onClick={() => {
        if (
          organizationIdName.organizationType ===
          EOrganizationType.PRO_VIEW_ORGANIZATION
        ) {
          mixpanel.track("User navigated to unit", {
            organization_id: organizationIdName.organizationId,
            organization_name: organizationIdName.organizationName,
            organization_type: organizationIdName.organizationType,
            unit_id: propertyId,
            unit_name: name,
            user_id: userId,
          });
        }
      }}
      type="button"
      className="unit-container"
    >
      {renderSideLine({
        hasAlerts,
        hasWarnings,
        lineClassName: "unit-line",
      })}
      <Link
        to={
          organizationIdName.organizationType ===
          EOrganizationType.PRO_VIEW_ORGANIZATION
            ? `/${organizationId}/home/${propertyId}`
            : `/${organizationId}/units/${propertyId}`
        }
        className="unit-link"
        onClick={() => {
          window.scrollTo(0, 0);
        }}
      >
        <div className="unit-card-description-container">
          <div className="flex-with-space-between">
            <div className="unit-card-description">
              <div className="unit-title unit-name">{name}</div>
              {renderDescription}
            </div>
            <img
              src={Arrow}
              alt="arrow"
              className={
                organizationIdName.organizationType ===
                EOrganizationType.BCC_ORGANIZATION
                  ? "unit-card-description-arrow-svg"
                  : ""
              }
            />
          </div>
        </div>
      </Link>
      <div className="center-align-as-row">
        {organizationIdName.organizationType !==
          EOrganizationType.PRO_VIEW_ORGANIZATION && (
          <div className={vacancyTagClass}>
            {isOccupied ? "Occupied" : "Vacant"}
          </div>
        )}
        {renderTags({
          alerts,
          subscriptionStatus: isBrilliantMaxEnabled
            ? mapSubscriptionStatus(subscription)
            : undefined,
          warnings,
        })}
      </div>
    </button>
  );
};

export default UnitCard;
